import {
  submitTriggerAgain,
  subscribeTriggerAgain,
  triggerAgainState,
} from './trigger-again';
import { useToast } from '@/components/v1/hooks/use-toast';
import { Button } from '@/components/v1/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/v1/ui/tooltip';
import useCanWrite from '@/hooks/use-can-write';
import { useCurrentTenantId } from '@/hooks/use-tenant';
import { useApiError } from '@/lib/hooks';
import { appRoutes } from '@/router';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { Play } from 'lucide-react';
import { useSyncExternalStore } from 'react';

export function TriggerAgainButton({
  sourceRunId,
  payloadsRestricted,
}: {
  sourceRunId: string;
  payloadsRestricted?: boolean;
}) {
  const { tenantId } = useCurrentTenantId();
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const { toast } = useToast();
  const { handleApiError } = useApiError({});

  const submission = useSyncExternalStore(
    subscribeTriggerAgain,
    () => triggerAgainState(tenantId, sourceRunId),
    () => 'idle',
  );

  const { mutate: triggerAgain, isPending } = useMutation({
    mutationKey: ['workflow-run:trigger-again', tenantId, sourceRunId],
    mutationFn: (source: { tenantId: string; sourceRunId: string }) =>
      submitTriggerAgain(source.tenantId, source.sourceRunId),
    retry: false,
    onError: (error) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (isAxiosError(error) && status === 501) {
        const description: unknown =
          error.response?.data?.errors?.[0]?.description;
        toast({
          title: 'Trigger again unavailable',
          description:
            typeof description === 'string' && description.trim()
              ? description
              : 'The backend does not support Trigger again. Ask an administrator to update the API and engine before retrying.',
        });
        return;
      }
      if (isAxiosError(error) && status && status < 500 && status !== 408) {
        handleApiError(error);
        return;
      }
      toast({
        title: 'Submission not confirmed',
        description: isAxiosError(error)
          ? 'Retry submission will check the same request without creating a duplicate.'
          : error.message,
      });
    },
  });

  const disabled =
    !canWrite ||
    payloadsRestricted ||
    isPending ||
    submission === 'unavailable';
  const label = isPending
    ? 'Submitting…'
    : submission === 'pending'
      ? 'Retry submission'
      : 'Trigger again';
  const storageError =
    submission === 'unavailable'
      ? 'This tab cannot read saved submissions. Check browser storage before trying again.'
      : undefined;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={storageError ? 0 : undefined}
            aria-label={storageError}
          >
            <Button
              size="sm"
              className="text-sm gap-2"
              variant="outline"
              disabled={disabled}
              onClick={() =>
                triggerAgain(
                  { tenantId, sourceRunId },
                  {
                    onSuccess: (runId, source) => {
                      navigate({
                        to: appRoutes.tenantRunRoute.to,
                        params: { tenant: source.tenantId, run: runId },
                        search: (prev) => ({
                          ...prev,
                          wasRedirectedFromTrigger: true,
                        }),
                      });
                    },
                  },
                )
              }
            >
              <Play className="size-4" />
              {label}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{storageError ?? label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
